/**
 * Shared-password gate for the manufacturing catalog.
 *
 * The catalog holds a vendor's PFMEA / Control Plan / WI documents, and the
 * vendor asked for the URL to sit behind a password. This is the same kind of
 * gate as the VITE_LOGIN_PASSWORD one in App.tsx: a UI gate, not authentication.
 * The password is baked into the JS bundle at build time and `/api/catalog`
 * still answers any authenticated portal session, so this keeps the pages out
 * of casual reach — it does not protect the data.
 *
 * The unlock survives reloads and browser restarts but is cleared alongside the
 * login session (see `clearCatalogUnlock` in App.tsx's verify-failure path), so
 * it never outlives the session that was allowed to see the catalog.
 */

/** Stores a non-secret marker (the unlock-version), never the password itself. */
const UNLOCK_KEY = 'cypremos.catalogUnlock'
const UNLOCK_VALUE = '1'

function configuredPassword(): string {
  return import.meta.env.VITE_MANUFACTURING_CATALOG_PASSWORD || ''
}

/** False when no password is configured — the local/dev default, gate disabled. */
export function isCatalogLockEnabled(): boolean {
  return configuredPassword() !== ''
}

export function isCatalogUnlocked(): boolean {
  if (!isCatalogLockEnabled()) return true
  try {
    return localStorage.getItem(UNLOCK_KEY) === UNLOCK_VALUE
  } catch {
    return false
  }
}

/** Returns whether `input` matched; only a match writes the unlock marker. */
export function unlockCatalog(input: string): boolean {
  const password = configuredPassword()
  if (!password || input !== password) return false
  try {
    localStorage.setItem(UNLOCK_KEY, UNLOCK_VALUE)
  } catch {
    // Storage unavailable (private mode): the caller still unlocks for this render.
  }
  return true
}

export function clearCatalogUnlock(): void {
  try {
    localStorage.removeItem(UNLOCK_KEY)
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
